/**
 * Canonical browser-storage keys (post white-label rename) + their legacy
 * `zion:` predecessors, plus the read-old/write-new migration primitive.
 *
 * The platform's localStorage / sessionStorage keys were renamed from the
 * `zion:` / `zion-` / `zion_` namespace to `app:` / `app-` / `app_` for full
 * white-label (DECISIONS D7). To avoid breaking in-flight user state across the
 * deploy, reads migrate a legacy value forward on first access via
 * `readMigrated`. The `LEGACY_*` map and the migration paths can be deleted one
 * release after this lands (no live session can still hold a legacy key by then).
 *
 * Moved from the shell's `platform/storage-keys.ts` (kernel apps, WS-14 section 2). The polarity
 * controller reads the polarity key here, and the saved-state and recents services of WS-14b read
 * the rest. In `@zion/surface-host` this module is the one home of the legacy `zion:` literals;
 * the shell's copy stays its own home until WS-16d deletes it.
 */

/** Canonical keys — what production reads and writes today. */
export const STORAGE_KEYS = {
  /** localStorage: display polarity preference. */
  polarity: 'app:shell:polarity',
  /** localStorage: guided-tour state mirror ('unseen' | 'completed' | 'dismissed'). */
  tourState: 'app:home:tourState',
  /** localStorage: admin onboarding-checklist dismissed flag. */
  onboardingDismissed: 'app-admin-onboarding-dismissed',
  /** sessionStorage: returnTo carried across the OAuth IdP round-trip. */
  returnTo: 'app_return_to',
  /** sessionStorage: per-app saved-state key prefix (`${prefix}${appId}`). */
  appStatePrefix: 'app:app-state:',
  /**
   * sessionStorage: the identity that owns the tab's routed saved state, the JSON array
   * `[userId, orgId]`, or `''` after a sign-out. It sits outside `appStatePrefix`, so
   * `clearStaleStates` never reads it. `bindSavedStateOwner` writes it (layered shell surfaces,
   * WS-8, R-80).
   */
  appStateOwner: 'app:app-state-owner',
  /** localStorage: capped open-event log mirror for the home "Jump back in" row. */
  recents: 'app:home:recents',
  /**
   * localStorage: the identity that owns the `recents` mirror, the JSON array `[userId, orgId]`,
   * or `''` after a sign-out. An absent key means a mirror written before the key existed. The
   * loader's recents controller writes it (layered shell surfaces, WS-8, R-80).
   */
  recentsOwner: 'app:home:recents-owner',
} as const;

/** Legacy keys — read-fallback only; never written. Remove after one release. */
export const LEGACY_STORAGE_KEYS = {
  polarity: 'zion:shell:polarity',
  onboardingDismissed: 'zion-admin-onboarding-dismissed',
  returnTo: 'zion_return_to',
  appStatePrefix: 'zion:app-state:',
} as const;

/**
 * Read a key with one-time forward migration: if the canonical key is absent but
 * the legacy key is present, copy legacy→canonical, delete the legacy entry, and
 * return the legacy value. Returns the canonical value, or null when neither
 * exists. Storage exceptions (private mode / disabled storage) are swallowed and
 * surface as null — callers treat null as "unset".
 */
export function readMigrated(
  storage: Storage,
  canonicalKey: string,
  legacyKey: string,
): string | null {
  try {
    const current = storage.getItem(canonicalKey);
    if (current !== null) return current;
    const legacy = storage.getItem(legacyKey);
    if (legacy === null) return null;
    storage.setItem(canonicalKey, legacy);
    storage.removeItem(legacyKey);
    return legacy;
  } catch {
    return null;
  }
}
